import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { contactInfo } from '../mock';
import { useToast } from '../hooks/use-toast';

export const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    toast({
      title: "Message Sent!",
      description: "Thank you for contacting us. We'll get back to you soon.",
    });
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      message: ''
    });
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center space-y-4 mb-16"
        >
          <span className="inline-block px-4 py-2 bg-[#2196F3]/10 text-[#2196F3] rounded-full text-sm font-semibold">
            Get In Touch
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-[#071B4D]">
            Let's Build Something Amazing Together
          </h2>
          <p className="max-w-3xl mx-auto text-lg text-gray-600">
            Ready to transform your business with cutting-edge technology?
            Contact us today for a consultation.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="space-y-6">
              <div className="flex items-start gap-4 p-6 bg-gradient-to-br from-white to-blue-50/50 rounded-xl border border-gray-200 hover:border-[#2196F3] transition-all duration-300">
                <div className="flex-shrink-0 w-12 h-12 bg-[#2196F3]/10 rounded-lg flex items-center justify-center">
                  <Mail className="w-6 h-6 text-[#2196F3]" />
                </div>
                <div>
                  <div className="font-semibold text-[#071B4D] mb-1">Email</div>
                  <a href={`mailto:${contactInfo.email}`} className="text-gray-600 hover:text-[#2196F3] transition-colors">
                    {contactInfo.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4 p-6 bg-gradient-to-br from-white to-blue-50/50 rounded-xl border border-gray-200 hover:border-[#2196F3] transition-all duration-300">
                <div className="flex-shrink-0 w-12 h-12 bg-[#2196F3]/10 rounded-lg flex items-center justify-center">
                  <Phone className="w-6 h-6 text-[#2196F3]" />
                </div>
                <div>
                  <div className="font-semibold text-[#071B4D] mb-1">Phone</div>
                  <a href={`tel:${contactInfo.phone}`} className="text-gray-600 hover:text-[#2196F3] transition-colors">
                    {contactInfo.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4 p-6 bg-gradient-to-br from-white to-blue-50/50 rounded-xl border border-gray-200 hover:border-[#2196F3] transition-all duration-300">
                <div className="flex-shrink-0 w-12 h-12 bg-[#2196F3]/10 rounded-lg flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-[#2196F3]" />
                </div>
                <div>
                  <div className="font-semibold text-[#071B4D] mb-1">Address</div>
                  <p className="text-gray-600">{contactInfo.address}</p>
                </div>
              </div>
            </div>

            {/* Map Placeholder */}
            <div className="w-full h-64 bg-gray-100 rounded-xl overflow-hidden border border-gray-200">
              <iframe
                title="Google Maps"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3502.0!2d77.4239356!3d28.6148638!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390cee4c806c12ed%3A0xa433e8a92e367c37!2sGalaxy%20N%20Ave%2C%20Gaur%20City%201%2C%20Sector%204%2C%20Ghaziabad%2C%20Uttar%20Pradesh%20201016!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
              ></iframe>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form onSubmit={handleSubmit} className="space-y-6 p-8 bg-gradient-to-br from-white to-blue-50/50 rounded-2xl border border-gray-200 shadow-xl">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-[#071B4D] mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#2196F3] focus:ring-2 focus:ring-[#2196F3]/20 outline-none transition-all"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-[#071B4D] mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#2196F3] focus:ring-2 focus:ring-[#2196F3]/20 outline-none transition-all"
                  placeholder="your.email@company.com"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-[#071B4D] mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#2196F3] focus:ring-2 focus:ring-[#2196F3]/20 outline-none transition-all"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-semibold text-[#071B4D] mb-2">
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#2196F3] focus:ring-2 focus:ring-[#2196F3]/20 outline-none transition-all"
                    placeholder="Your company"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-[#071B4D] mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#2196F3] focus:ring-2 focus:ring-[#2196F3]/20 outline-none transition-all resize-none"
                  placeholder="Tell us about your project..."
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full px-8 py-4 bg-[#2196F3] text-white font-semibold rounded-lg shadow-lg hover:bg-[#1976D2] hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 group"
              >
                Send Message
                <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};